import { Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Subscription, from, of } from 'rxjs';
import { concatMap, map } from 'rxjs/operators';

import { GeoQuery, GeoService, MAP_MAX_ZOOM, MAP_MIN_ZOOM, TILE_SIZE } from '@app/shared/geo.service';
import { DeliveryPlanning } from '../../logistic.service';
import { BlPreview, DelrunsService } from '../delruns.service';
import { BL_CLOSE_GRACE_MS, BL_HOVER_DELAY_MS } from '@app/shared/constants';

// Point à afficher sur la carte (construit par le parent depuis les tournées + « À planifier »)
export interface MapPoint {
	address: GeoQuery;		// requête de géocodage prête (GeoService.geoQueryOf) — BAN via DelrunsService.geocode$, cache localStorage
	lat?: number | null;	// coordonnées déjà en base (cufs Latitude/Longitude de l'adresse) : prioritaires, BAN inutile
	lon?: number | null;
	label: string;				// tooltip du marqueur
	color: string;				// couleur de la tournée (gris = non affecté)
	seq: number | null;		// n° d'ordre dans la tournée (null = non affecté)
	ref?: string | null;	// repère de carte (A, B, … AA) : identité de la livraison TANT QU'ELLE n'est pas
												// dans une tournée — une fois planifiée, seq prend le relais (voir pinTextOf)
	delivery?: DeliveryPlanning;	// pour l'aperçu BL au survol de l'épingle
}

// text / fontSize / baseline sont figés à la création du marqueur : ils ne dépendent que du point,
// jamais du viewport — inutile de les recalculer à chaque pan.
interface MapMarker extends MapPoint { lat: number; lon: number; left: number; top: number; text: string; fontSize: number; baseline: number; }
interface MapTile { url: string; left: number; top: number; key: string; }

// Aperçu BL affiché au survol d'une épingle (1 s), ancré dans le cadre de la carte
interface MarkerHover {
	m: MapMarker;
	left: number;
	top: number;
	loading: boolean;
	error: boolean;
	preview: BlPreview | null;
	details: string[];		// adresse complète (jamais tronquée) + cufs d'adresse valués
	tel: string;
}

const HOVER_PANEL_W = 280;
const HOVER_PANEL_MAX_H = 340;		// hauteur max du panneau (scroll interne au-delà) — voir scss .map-hover

// Carte OSM légère SANS dépendance npm : mosaïque de tuiles statiques (config GeoService)
// + marqueurs positionnés en projection Web Mercator (GeoService). Interactive : pan (glisser),
// zoom boutons ±, Ctrl+molette et double-clic (la molette seule garde le scroll de la page) ;
// dès que l'utilisateur a bougé la vue, le recadrage automatique (fit) est désactivé.
// Survol d'une épingle 1 s => aperçu BL (mêmes données que les cartes du board, cache par commande).
@Component({
	selector: 'app-delruns-map',
	imports: [MatButtonModule, MatIconModule],
	templateUrl: './delruns-map.component.html',
	styleUrl: './delruns-map.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: true,
})
export class DelrunsMapComponent implements OnChanges, OnDestroy {

	@Input() points: MapPoint[] = [];
	@Input() height = 440;

	private delrunsService = inject(DelrunsService);
	private host: ElementRef<HTMLElement> = inject(ElementRef);
	private geocodeSubscription?: Subscription;

	public markers: MapMarker[] = [];
	public failed: MapPoint[] = [];			// adresses non géolocalisées
	public tiles: MapTile[] = [];
	public zoom = 6;
	public centerLat = 46.6;						// centre France par défaut
	public centerLon = 2.4;
	public done = 0;
	public total = 0;
	public drag: { startX: number; startY: number; baseX: number; baseY: number } | null = null;		// pan en cours
	private userMoved = false;					// l'utilisateur a bougé/zoomé : ne plus recadrer automatiquement
	public hover: MarkerHover | null = null;
	private hoverTimer: ReturnType<typeof setTimeout> | null = null;
	private closeTimer: ReturnType<typeof setTimeout> | null = null;
	private hoverSub?: Subscription;

	ngOnChanges(changes: SimpleChanges): void {
		if (!changes['points']) {
			if (changes['height']) this.render();		// redimensionnement (fenêtre / carte agrandie) : recalcul de la mosaïque
			return;
		}
		this.geocodeSubscription?.unsubscribe();
		this.closeHover();
		this.userMoved = false;		// nouveau jeu de points (rechargement du board) : recadrage auto réactivé
		this.markers = [];
		this.failed = [];
		this.done = 0;
		this.total = this.points?.length ?? 0;
		this.render();
		// Coordonnées déjà en base (cufs Latitude/Longitude) : utilisées telles quelles, sans appel BAN.
		// Sinon géocodage séquentiel : les adresses en cache répondent immédiatement,
		// les appels réseau BAN sont rapides (pas de rate limit contraignant).
		this.geocodeSubscription = from(this.points ?? [])
			.pipe(concatMap((p) => (p.lat != null && p.lon != null ? of({ lat: +p.lat, lon: +p.lon }) : this.delrunsService.geocode$(p.address)).pipe(map((gp) => ({ p, gp })))))
			.subscribe(({ p, gp }) => {
				this.done++;
				if (gp) {
					this.markers.push({ ...p, lat: gp.lat, lon: gp.lon, left: 0, top: 0, ...DelrunsMapComponent.pinTextOf(p) });
					this.fit();
					this.render();
				} else {
					this.failed.push(p);
				}
			});
	}

	ngOnDestroy(): void {
		this.geocodeSubscription?.unsubscribe();
		this.closeHover();
	}

	get loading(): boolean {
		return this.done < this.total;
	}

	// Contenu du cercle de l'épingle : le n° d'arrêt dès que la livraison est dans une tournée
	// (0 = dépôt de départ), sinon son repère de carte. Chiffre et lettre ne se confondent jamais :
	// l'alphabet dit à lui seul « planifiée » ou « à planifier », en plus de la couleur.
	// Le cercle fait 18 px de diamètre : au-delà d'un caractère, le texte doit rapetisser.
	static pinTextOf(p: MapPoint): { text: string; fontSize: number; baseline: number } {
		const text = String(p.seq ?? p.ref ?? '•');
		const fontSize = text.length >= 3 ? 8 : text.length === 2 ? 9 : 11;
		return { text, fontSize, baseline: 13 + fontSize * 0.35 };		// 13 = centre du cercle dans le viewBox
	}

	zoomBy(dz: number): void {
		this.userMoved = true;		// zoom manuel : la vue appartient à l'utilisateur
		this.closeHover();				// les épingles bougent : un panneau resté ouvert serait mal ancré
		this.zoom = Math.min(MAP_MAX_ZOOM, Math.max(MAP_MIN_ZOOM, this.zoom + dz));
		this.render();
	}

	// Ctrl + molette = zoom (convention cartes web) ; molette seule = scroll normal de la page
	onWheel(ev: WheelEvent): void {
		if (!ev.ctrlKey) return;
		ev.preventDefault();
		this.zoomBy(ev.deltaY < 0 ? 1 : -1);
	}

	// Double-clic = zoom avant (ignoré sur les boutons et le panneau d'aperçu)
	onDblClick(ev: MouseEvent): void {
		if ((ev.target as HTMLElement)?.closest?.('button, .map-hover')) return;
		ev.preventDefault();
		this.zoomBy(1);
	}

	// --- Pan à la souris / au doigt (Pointer Events) ---

	onPanDown(ev: PointerEvent): void {
		if ((ev.target as HTMLElement)?.closest?.('button, .map-hover')) return;		// boutons zoom et panneau restent cliquables
		ev.preventDefault();
		this.closeHover();
		try { (ev.target as Element)?.setPointerCapture?.(ev.pointerId); } catch { /* runner de test sans pointeur réel */ }
		this.drag = {
			startX: ev.clientX, startY: ev.clientY,
			baseX: GeoService.lonToX(this.centerLon, this.zoom),
			baseY: GeoService.latToY(this.centerLat, this.zoom),
		};
	}

	onMove(ev: PointerEvent): void {
		if (!this.drag) return;
		this.userMoved = true;
		this.centerLon = GeoService.xToLon(this.drag.baseX - (ev.clientX - this.drag.startX), this.zoom);
		this.centerLat = GeoService.yToLat(this.drag.baseY - (ev.clientY - this.drag.startY), this.zoom);
		this.render();
	}

	onUp(_ev: PointerEvent): void {
		this.drag = null;
	}

	private frameWidth(): number {
		return this.host?.nativeElement?.clientWidth || 900;
	}

	// Cadre la vue sur l'ensemble des marqueurs (bbox + marge) — désactivé dès que l'utilisateur a bougé
	fit(): void {
		if (this.userMoved || !this.markers.length) return;
		const lats = this.markers.map((m) => m.lat);
		const lons = this.markers.map((m) => m.lon);
		const minLat = Math.min(...lats), maxLat = Math.max(...lats);
		const minLon = Math.min(...lons), maxLon = Math.max(...lons);
		this.centerLat = (minLat + maxLat) / 2;
		this.centerLon = (minLon + maxLon) / 2;
		const w = this.frameWidth() - 80, h = this.height - 80;
		let z = MAP_MAX_ZOOM;
		for (; z > MAP_MIN_ZOOM; z--) {
			const spanX = GeoService.lonToX(maxLon, z) - GeoService.lonToX(minLon, z);
			const spanY = GeoService.latToY(minLat, z) - GeoService.latToY(maxLat, z);
			if (spanX <= w && spanY <= h) break;
		}
		this.zoom = Math.min(z, 15);		// un marqueur seul ne zoome pas à la rue près
	}

	// (Re)calcule la mosaïque de tuiles et les positions px des marqueurs pour le viewport courant
	render(): void {
		const W = this.frameWidth(), H = this.height, z = this.zoom;
		const n = Math.pow(2, z);
		const left0 = GeoService.lonToX(this.centerLon, z) - W / 2;
		const top0 = GeoService.latToY(this.centerLat, z) - H / 2;

		const tiles: MapTile[] = [];
		for (let tx = Math.floor(left0 / TILE_SIZE); tx <= Math.floor((left0 + W) / TILE_SIZE); tx++) {
			for (let ty = Math.floor(top0 / TILE_SIZE); ty <= Math.floor((top0 + H) / TILE_SIZE); ty++) {
				if (ty < 0 || ty >= n) continue;							// pas de tuile au-delà des pôles
				const wrappedX = ((tx % n) + n) % n;					// wrap de la longitude
				tiles.push({
					url: GeoService.tileUrlOf(z, wrappedX, ty),
					left: tx * TILE_SIZE - left0,
					top: ty * TILE_SIZE - top0,
					key: `${z}/${tx}/${ty}`,
				});
			}
		}
		this.tiles = tiles;
		for (const m of this.markers) {
			m.left = GeoService.lonToX(m.lon, z) - left0;
			m.top = GeoService.latToY(m.lat, z) - top0;
		}
	}

	// ============================================================
	// Aperçu BL au survol d'une épingle (mêmes données que les cartes du board)
	// ============================================================

	onMarkerEnter(m: MapMarker): void {
		if (this.drag) return;		// jamais de panneau pendant un pan
		this.cancelCloseTimer();
		if (this.hover?.m === m) return;
		this.cancelHoverTimer();
		this.hoverTimer = setTimeout(() => this.openHover(m), BL_HOVER_DELAY_MS);
	}

	onMarkerLeave(): void {
		this.cancelHoverTimer();
		this.scheduleCloseHover();
	}

	onHoverPanelEnter(): void { this.cancelCloseTimer(); }		// délai de grâce : passer de l'épingle au panneau

	onHoverPanelLeave(): void { this.scheduleCloseHover(); }

	private openHover(m: MapMarker): void {
		const d = m.delivery;
		if (!d) return;
		const addr = DelrunsService.deliveryAddressOf(d);
		this.hover = {
			m,
			left: Math.min(Math.max(m.left + 14, 8), Math.max(8, this.frameWidth() - HOVER_PANEL_W - 8)),
			top: Math.min(Math.max(m.top - 48, 8), Math.max(8, this.height - HOVER_PANEL_MAX_H - 8)),		// le panneau reste entier dans le cadre
			loading: true,
			error: false,
			preview: null,
			details: this.delrunsService.addressDetails(addr),
			tel: d.lnk_order?.lnk_customer?.tel1 || d.lnk_order?.lnk_customer?.tel2 || '',
		};
		this.hoverSub?.unsubscribe();
		this.hoverSub = this.delrunsService.getOrderWithBls$(d.id_order).subscribe({
			next: (order) => {
				if (!this.hover || this.hover.m !== m) return;
				this.hover.loading = false;
				this.hover.preview = DelrunsService.blPreviewOf(order, d.id);
				if (!this.hover.preview) this.hover.error = true;		// BL introuvable dans la commande (défensif)
			},
			error: () => {
				if (!this.hover || this.hover.m !== m) return;
				this.hover.loading = false;
				this.hover.error = true;
			},
		});
	}

	closeHover(): void {
		this.cancelHoverTimer();
		this.cancelCloseTimer();
		this.hoverSub?.unsubscribe();
		this.hover = null;
	}

	private scheduleCloseHover(): void {
		this.cancelCloseTimer();
		this.closeTimer = setTimeout(() => this.closeHover(), BL_CLOSE_GRACE_MS);
	}

	private cancelHoverTimer(): void {
		if (this.hoverTimer) { clearTimeout(this.hoverTimer); this.hoverTimer = null; }
	}

	private cancelCloseTimer(): void {
		if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null; }
	}

	// Le back renvoie des qtés float : 12.0 => « 12 », 2.5 => « 2,5 »
	qtyOf(n: number | null): string {
		return n === null || n === undefined ? '' : String(+n).replace('.', ',');
	}
}
